"use client";

import { type FormEvent, type ReactNode, useState } from "react";

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
      {children}
    </label>
  );
}

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    setStatus("loading");
    setErrorMsg("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fd.get("name"),
          email: fd.get("email"),
          phone: fd.get("phone") ?? "",
          message: fd.get("message"),
        }),
      });

      const data = (await res.json()) as { ok?: boolean; error?: string };

      if (!res.ok || !data.ok) {
        setStatus("error");
        setErrorMsg(data.error ?? "Something went wrong. Please try again.");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  }

  const inputClass =
    "w-full rounded-2xl border border-zinc-700/90 bg-zinc-950/80 px-4 py-3.5 text-sm text-zinc-100 outline-none ring-0 transition-[border-color,box-shadow] placeholder:text-zinc-600 focus:border-[#b89a5b]/70 focus:shadow-[0_0_0_3px_rgba(184,154,91,0.2)] disabled:opacity-55";

  return (
    <div className="relative rounded-2xl border border-zinc-800/90 bg-black/50 p-6 shadow-xl shadow-black/40 backdrop-blur-sm sm:p-8">
      <div
        role="status"
        aria-live="polite"
        className={`mb-6 overflow-hidden rounded-2xl border transition-[max-height,opacity] duration-500 ease-out ${
          status === "success"
            ? "contact-alert-visible max-h-48 border-emerald-500/45 bg-emerald-950/35 opacity-100"
            : "pointer-events-none max-h-0 border-transparent opacity-0"
        }`}
      >
        <div className="contact-alert-inner flex gap-3 px-4 py-4 sm:px-5 sm:py-5">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <div>
            <p className="font-semibold text-emerald-100">Message sent</p>
            <p className="mt-1 text-sm leading-relaxed text-emerald-100/85">
              Our team will review your message and reach out using the details you provided.
            </p>
          </div>
        </div>
      </div>

      {status === "error" ? (
        <div
          role="alert"
          className="mb-6 rounded-2xl border border-red-500/35 bg-red-950/25 px-4 py-3 text-sm text-red-200"
        >
          {errorMsg}
        </div>
      ) : null}

      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="contact-name">Full name</FieldLabel>
            <input
              id="contact-name"
              name="name"
              required
              autoComplete="name"
              placeholder="John Doe"
              disabled={status === "loading"}
              className={inputClass}
            />
          </div>
          <div>
            <FieldLabel htmlFor="contact-email">Email</FieldLabel>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@company.com"
              disabled={status === "loading"}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <FieldLabel htmlFor="contact-phone">Phone</FieldLabel>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="+1 (555) 000-0000"
            disabled={status === "loading"}
            className={inputClass}
          />
        </div>

        <div>
          <FieldLabel htmlFor="contact-message">Message</FieldLabel>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={5}
            placeholder="How can we help?"
            disabled={status === "loading"}
            className={`${inputClass} min-h-[140px] resize-y`}
          />
        </div>

        <button
          type="submit"
          disabled={status === "loading"}
          className="group relative mt-2 flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-r from-[#b89a5b] to-[#b89a5b] px-5 py-4 text-sm font-semibold text-zinc-950 shadow-lg shadow-[#b89a5b]/35 transition hover:from-[#b89a5b]/90 hover:to-[#b89a5b]/90 disabled:cursor-wait disabled:opacity-70"
        >
          <span>{status === "loading" ? "Sending…" : "Send message"}</span>
          {status !== "loading" ? (
            <svg
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          ) : null}
        </button>
      </form>
    </div>
  );
}
